/**
 * Parse a YYYY-MM-DD date string as local midnight instead of UTC midnight.
 *
 * JavaScript's Date constructor interprets "YYYY-MM-DD" as UTC midnight,
 * which causes dates to shift back one day in timezones west of UTC.
 * Appending "T00:00:00" forces local-time interpretation.
 */
export function parseLocalDate(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00');
}

/** Local midnight at the start of the given moment (defaults to now). */
export function startOfToday(now: Date = new Date()): Date {
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  return todayStart;
}

/**
 * True when a YYYY-MM-DD session date is today or later in local time.
 * Sessions dated today stay "upcoming" all day and drop off at midnight.
 *
 * Pass an explicit todayStart when the caller memoizes the result — the
 * default reads the clock, which a memo cannot see change at midnight.
 */
export function isUpcomingSession(
  sessionDate: string,
  todayStart: Date = startOfToday()
): boolean {
  return parseLocalDate(sessionDate).getTime() >= todayStart.getTime();
}

/** Compare two sessions by date, earliest first. */
export function compareSessionDateAsc(
  a: { sessionDate: string },
  b: { sessionDate: string }
): number {
  return (
    parseLocalDate(a.sessionDate).getTime() -
    parseLocalDate(b.sessionDate).getTime()
  );
}

/**
 * Return only upcoming, still-scheduled sessions: status === 'scheduled'
 * AND sessionDate today-or-later (local time), sorted earliest-first.
 * Hides sessions that have already occurred. Mirrors the AdminDashboard
 * "Upcoming Sessions" logic (PR #239) so every surface behaves identically.
 *
 * Customer-facing surfaces want both filters. Admin management surfaces want
 * the date filter only — a cancelled or completed session still has to be
 * editable — so they use isUpcomingSession() directly.
 */
export function getUpcomingScheduledSessions<
  T extends { sessionDate: string; status: string }
>(schedules: T[]): T[] {
  const todayStart = startOfToday();
  return schedules
    .filter(
      (s) => s.status === 'scheduled' && isUpcomingSession(s.sessionDate, todayStart)
    )
    .sort(compareSessionDateAsc);
}

/**
 * Format a YYYY-MM-DD date string for display, correctly handling timezone.
 */
export function formatDate(
  dateStr: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return parseLocalDate(dateStr).toLocaleDateString('en-US', options);
}

const TZ_ABBREVIATIONS: Record<string, string> = {
  PT: 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  MT: 'America/Denver',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  CT: 'America/Chicago',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  ET: 'America/New_York',
  EST: 'America/New_York',
  EDT: 'America/New_York',
};

/**
 * Parse display-format session times like "9:00 AM PT" into 24-hour time
 * and an IANA timezone suitable for calendar APIs. Falls back to
 * America/Los_Angeles if the abbreviation is missing or unrecognized.
 */
export function parseSessionTime(timeStr: string): { time: string; timeZone: string } {
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)\s*([A-Z]{2,4})?$/i);
  if (!match) {
    return { time: timeStr, timeZone: 'America/Los_Angeles' };
  }
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const meridiem = match[3].toUpperCase();
  const tzAbbrev = (match[4] || 'PT').toUpperCase();
  if (meridiem === 'PM' && hour !== 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  return {
    time: `${String(hour).padStart(2, '0')}:${minute}`,
    timeZone: TZ_ABBREVIATIONS[tzAbbrev] || 'America/Los_Angeles',
  };
}
